import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, HardHat, Zap, Truck, GraduationCap, Monitor, Phone, MapPin, Mail, ShieldCheck, Lightbulb, Handshake, LogIn } from "lucide-react";
import { api, CATEGORIES } from "@/lib/api";
import { RequestForm } from "@/components/RequestForm";
import { ChatWidget } from "@/components/ChatWidget";

const ICONS = { genie_civil: HardHat, genie_electrique: Zap, transport: Truck, formation: GraduationCap, informatique: Monitor };
const VALUES = [[ShieldCheck, "Intégrité"], [Lightbulb, "Innovation"], [Handshake, "Fiabilité"]];

const Header = () => (
  <header className="sticky top-0 z-30 border-b border-slate-200 bg-white/85 backdrop-blur-xl">
    <div className="mx-auto flex h-20 max-w-7xl items-center justify-between px-5 sm:px-8">
      <img src="/logo.png" alt="ACES" className="h-12 w-auto" data-testid="landing-logo" />
      <nav className="hidden items-center gap-8 text-sm font-medium text-slate-700 md:flex">
        <a href="#services" className="hover:text-red-600" data-testid="landing-nav-services">Nos services</a>
        <a href="#realisations" className="hover:text-red-600" data-testid="landing-nav-gallery">Réalisations</a>
        <a href="#apropos" className="hover:text-red-600" data-testid="landing-nav-about">À propos</a>
        <a href="#demande" className="hover:text-red-600" data-testid="landing-nav-request">Demande de prestation</a>
        <a href="#contact" className="hover:text-red-600" data-testid="landing-nav-contact">Contact</a>
      </nav>
      <Link to="/login" className="flex items-center gap-2 rounded-md border border-slate-300 px-4 py-2 text-sm font-semibold text-slate-800 transition-colors hover:border-slate-900" data-testid="landing-staff-login">
        <LogIn className="h-4 w-4" /> <span className="hidden sm:inline">Espace personnel</span>
      </Link>
    </div>
  </header>
);

const Hero = () => (
  <section className="relative overflow-hidden bg-[#0F172A]">
    <img src="/photos/equipe.webp" alt="Équipe ACES sur chantier" className="absolute inset-0 h-full w-full object-cover object-[center_30%] opacity-50" />
    <div className="absolute inset-0 bg-gradient-to-r from-[#0F172A] via-[#0F172A]/80 to-transparent" />
    <div className="relative mx-auto max-w-7xl px-5 py-24 sm:px-8 sm:py-32">
      <p className="mb-4 text-xs font-semibold uppercase tracking-[0.3em] text-red-400 fade-up">African Cooperation Engineering Service — Likasi, RDC</p>
      <h1 className="max-w-3xl font-display text-4xl font-extrabold leading-tight text-white sm:text-5xl lg:text-6xl fade-up">
        L'ingénierie qui maintient vos ouvrages et vos machines en état de servir.
      </h1>
      <p className="mt-6 max-w-xl text-base text-slate-300 fade-up">
        Génie civil, génie électrique et maintenance industrielle, transport et logistique, formation et informatique : un seul partenaire pour vos projets techniques.
      </p>
      <div className="mt-10 flex flex-wrap gap-3 fade-up">
        <a href="#demande" className="group flex items-center gap-2 rounded-md bg-red-600 px-6 py-3 text-sm font-semibold text-white transition-colors hover:bg-red-700" data-testid="hero-request-btn">
          Demander une prestation <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
        </a>
        <a href="#services" className="rounded-md border border-white/30 px-6 py-3 text-sm font-semibold text-white transition-colors hover:bg-white/10" data-testid="hero-services-btn">Découvrir nos services</a>
        <Link to="/formation/inscription" className="rounded-md bg-white px-6 py-3 text-sm font-semibold text-slate-900 transition-colors hover:bg-slate-100" data-testid="hero-training-btn">S'inscrire à une formation</Link>
      </div>
    </div>
  </section>
);

const ServicesSection = ({ services, onPick }) => (
  <section id="services" className="mx-auto max-w-7xl scroll-mt-20 px-5 py-24 sm:px-8">
    <p className="text-xs font-semibold uppercase tracking-[0.3em] text-red-600">Nos services</p>
    <h2 className="mt-2 max-w-2xl font-display text-3xl font-bold text-slate-900 sm:text-4xl">Cinq pôles d'expertise au service de vos projets</h2>
    <div className="mt-12 grid gap-px overflow-hidden rounded-md border border-slate-200 bg-slate-200 md:grid-cols-2 lg:grid-cols-3">
      {Object.entries(CATEGORIES).map(([k, c]) => {
        const Icon = ICONS[k];
        const list = services.filter((s) => s.category === k);
        return (
          <div key={k} className="group bg-white p-8 transition-colors hover:bg-slate-50" data-testid={`landing-category-${k}`}>
            <div className="flex h-12 w-12 items-center justify-center rounded-md text-white" style={{ background: c.color }}><Icon className="h-6 w-6" /></div>
            <h3 className="mt-5 font-display text-xl font-bold text-slate-900">{c.label}</h3>
            <ul className="mt-4 space-y-2">
              {list.map((s) => (
                <li key={s.id}>
                  <button onClick={() => onPick(s.id)} className="flex w-full items-start gap-2 text-left text-sm text-slate-600 hover:text-red-600" data-testid={`landing-service-${s.id}`}>
                    <ArrowRight className="mt-0.5 h-3.5 w-3.5 shrink-0 text-slate-300 group-hover:text-red-500" /> {s.name}
                  </button>
                </li>
              ))}
            </ul>
          </div>
        );
      })}
      <div className="flex flex-col justify-between bg-[#0F172A] p-8 text-white">
        <p className="font-display text-xl font-bold">Un besoin spécifique ?</p>
        <p className="mt-3 text-sm text-slate-300">D'autres prestations peuvent être étudiées sur demande. Décrivez-nous votre projet.</p>
        <a href="#demande" className="mt-6 inline-flex items-center gap-2 text-sm font-semibold text-red-400 hover:text-red-300" data-testid="landing-custom-request">Nous écrire <ArrowRight className="h-4 w-4" /></a>
      </div>
    </div>
  </section>
);

const GALLERY = [
  ["/photos/ferraillage.jpg", "Ferraillage de dalle et gaines électriques", "Génie civil & électrique", "md:col-span-2 md:row-span-2"],
  ["/photos/solaire.jpg", "Installation de panneaux solaires", "Génie électrique", ""],
  ["/photos/industrie-informatique.jpg", "Industrie et supervision informatique", "Maintenance & informatique", ""],
  ["/photos/equipe.webp", "Nos équipes sur le terrain", "Main d'œuvre", "md:col-span-2"],
  ["/photos/supervision.webp", "Supervision technique et contrôle des matériaux", "Génie civil", "md:col-span-3 md:row-span-2"],
  ["/photos/charpente.webp", "Montage de charpente métallique", "Construction", "md:col-span-1 md:row-span-2"],
];

const Gallery = () => (
  <section id="realisations" className="mx-auto max-w-7xl scroll-mt-20 px-5 pb-24 sm:px-8">
    <p className="text-xs font-semibold uppercase tracking-[0.3em] text-red-600">Nos réalisations</p>
    <h2 className="mt-2 font-display text-3xl font-bold text-slate-900 sm:text-4xl">Sur le terrain avec A.C.E.S</h2>
    <div className="mt-10 grid auto-rows-[220px] gap-3 md:grid-cols-4">
      {GALLERY.map(([src, title, tag, span]) => (
        <figure key={src} className={`group relative overflow-hidden rounded-md bg-slate-200 ${span}`} data-testid={`gallery-${src.split("/").pop().split(".")[0]}`}>
          <img src={src} alt={title} loading="lazy" className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105" />
          <figcaption className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/80 to-transparent p-4 text-white">
            <p className="text-[10px] font-semibold uppercase tracking-[0.2em] text-red-300">{tag}</p>
            <p className="text-sm font-semibold">{title}</p>
          </figcaption>
        </figure>
      ))}
    </div>
  </section>
);

const About = () => (
  <section id="apropos" className="scroll-mt-20 bg-white">
    <div className="mx-auto grid max-w-7xl items-center gap-12 px-5 py-24 sm:px-8 lg:grid-cols-2">
      <div className="grid grid-cols-2 gap-3" data-testid="about-direction">
        {[["NGOY KYUMA Joseph", "Directeur Général", "/photos/directeur-general.jpg"], ["Jean Luc LWAMBA", "Directeur des opérations", "/photos/direction.jpg"]].map(([n, t, src]) => (
          <figure key={n} className="overflow-hidden rounded-md border border-slate-200 bg-white" data-testid={`director-${n.split(" ")[0].toLowerCase()}`}>
            {src ? (
              <img src={src} alt={`${n}, ${t}`} className="h-80 w-full object-cover object-[center_20%] sm:h-96" />
            ) : (
              <div className="flex h-80 w-full flex-col items-center justify-center bg-[#0F172A] text-white sm:h-96">
                <span className="font-display text-5xl font-bold">{n.split(" ").slice(0, 2).map((w) => w[0]).join("")}</span>
                <span className="mt-2 text-[11px] uppercase tracking-[0.2em] text-slate-400">Photo à venir</span>
              </div>
            )}
            <figcaption className="border-t-4 border-red-600 px-3 py-3">
              <p className="text-sm font-bold text-slate-900">{n}</p>
              <p className="text-xs text-slate-500">{t}</p>
            </figcaption>
          </figure>
        ))}
      </div>
      <div>
        <p className="text-xs font-semibold uppercase tracking-[0.3em] text-red-600">À propos</p>
        <h2 className="mt-2 font-display text-3xl font-bold text-slate-900 sm:text-4xl">A.C.E.S S.A.R.L</h2>
        <p className="mt-5 text-slate-600">
          Notre société a pour objet d'assurer la maintenabilité de l'ensemble des actions techniques, administratives et de management durant le cycle de vie d'un bien, afin de le maintenir ou de le rétablir dans un état lui permettant d'accomplir la fonction requise.
        </p>
        <p className="mt-4 border-l-4 border-blue-900 bg-slate-50 py-3 pl-4 pr-3 text-slate-700" data-testid="about-ai-text">
          Intégrez l'intelligence artificielle dans votre quotidien professionnel : découvrez nos solutions d'assistants IA, de configuration d'agents virtuels et d'accompagnement informatique à l'ère du numérique.
        </p>
        <div className="mt-8 grid grid-cols-3 gap-4">
          {VALUES.map(([Icon, l]) => (
            <div key={l} className="border-t-4 border-red-600 pt-3">
              <Icon className="h-5 w-5 text-blue-900" />
              <p className="mt-2 text-sm font-semibold text-slate-900">{l}</p>
            </div>
          ))}
        </div>
      </div>
    </div>
  </section>
);

const Contact = ({ c }) => (
  <footer id="contact" className="scroll-mt-20 bg-[#0F172A] text-slate-300">
    <div className="mx-auto grid max-w-7xl gap-10 px-5 py-16 sm:px-8 md:grid-cols-3">
      <div>
        <div className="inline-block rounded-md bg-white p-2"><img src="/logo.png" alt="ACES" className="h-12 w-auto" /></div>
        <p className="mt-4 text-sm">{c.full_name}</p>
      </div>
      <div className="space-y-3 text-sm">
        <p className="flex gap-2"><MapPin className="h-4 w-4 shrink-0 text-red-500" /> {c.address}, {c.city}</p>
        {[c.phone, c.phone2].filter(Boolean).map((p) => <p key={p} className="flex gap-2"><Phone className="h-4 w-4 text-red-500" /> <a href={`tel:${p.replace(/\s/g, "")}`} className="hover:text-white">{p}</a></p>)}
        {c.email && <p className="flex gap-2"><Mail className="h-4 w-4 text-red-500" /> <a href={`mailto:${c.email}`} className="hover:text-white">{c.email}</a></p>}
      </div>
      <div className="text-sm md:text-right">
        <a href="#demande" className="font-semibold text-white hover:text-red-400">Demander une prestation →</a>
        <p className="mt-6 text-xs text-slate-500">© {new Date().getFullYear()} {c.name}. Tous droits réservés.</p>
      </div>
    </div>
  </footer>
);

export default function Landing() {
  const [site, setSite] = useState({ company: {}, services: [] });
  const [serviceId, setServiceId] = useState("");
  useEffect(() => { api.get("/public/site").then((r) => setSite(r.data)); }, []);

  const pick = (id) => {
    setServiceId(id);
    document.getElementById("demande")?.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <div className="bg-[#F8FAFC]">
      <Header />
      <Hero />
      <ServicesSection services={site.services} onPick={pick} />
      <Gallery />
      <About />
      <section id="demande" className="grid-bg scroll-mt-20 bg-slate-100">
        <div className="mx-auto grid max-w-7xl gap-12 px-5 py-24 sm:px-8 lg:grid-cols-[1fr_1.4fr]">
          <div>
            <p className="text-xs font-semibold uppercase tracking-[0.3em] text-red-600">Demande de prestation</p>
            <h2 className="mt-2 font-display text-3xl font-bold text-slate-900 sm:text-4xl">Parlez-nous de votre projet</h2>
            <p className="mt-5 text-slate-600">Remplissez ce formulaire : votre demande est transmise directement à notre équipe commerciale, qui étudie votre besoin et vous adresse un devis.</p>
            <ol className="mt-8 space-y-4 text-sm">
              {["Envoi de votre demande", "Étude par notre équipe", "Devis validé par la direction", "Réalisation de la prestation"].map((s, i) => (
                <li key={s} className="flex items-center gap-3"><span className="flex h-7 w-7 items-center justify-center rounded-full bg-blue-900 font-mono text-xs text-white">{i + 1}</span>{s}</li>
              ))}
            </ol>
          </div>
          <RequestForm services={site.services} serviceId={serviceId} setServiceId={setServiceId} />
        </div>
      </section>
      <Contact c={site.company} />
      <ChatWidget />
    </div>
  );
}
